import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import { 
  FileText, 
  Upload, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  Sparkles,
  Briefcase,
  Hammer,
  Package,
  Wrench,
  ClipboardList,
  ArrowRight,
  Menu,
  X
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SynthesePublicPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileData, setFileData] = useState(null);
  const [domain, setDomain] = useState("");
  const [documentType, setDocumentType] = useState("rc");
  const [mode, setMode] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(",")[1];
      setFileData(base64);
      setFileName(f.name);
      setFile(f);
      toast.success(`Document "${f.name}" chargé`);
    };
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) {
      const event = { target: { files: [f] } };
      handleFileChange(event);
    }
  };

  const handleAnalyze = async () => {
    if (!fileData || !domain || !documentType) {
      toast.error("Veuillez sélectionner un fichier et les options d'analyse");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      // Use public endpoint (no auth required)
      const response = await axios.post(`${API_URL}/api/analyze-document-public`, {
        file_data: fileData,
        file_name: fileName,
        domain,
        document_type: documentType,
        mode: mode,
        custom_prompt: ""
      }, {
        timeout: 180000,  // 3 minutes for large PDFs
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      if (response.data.success) {
        setResult(response.data.result);
        const remaining = response.data.remaining_analyses;
        if (remaining !== undefined) {
          toast.success(`Analyse terminée ! (${remaining} analyse${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''} aujourd'hui)`);
        } else {
          toast.success("Analyse terminée !");
        }
      } else {
        throw new Error(response.data.detail || "Erreur d'analyse");
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Erreur lors de l'analyse";
      setError(message);
      if (message.includes("Limite atteinte") || message.includes("429") || err.response?.status === 429) {
        toast.error("Limite atteinte (3/jour). Créez un compte pour continuer !");
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setFileName("");
    setFileData(null);
    setResult(null);
    setError("");
    setMode("standard");
  };

  const domains = [
    { id: "travaux", label: "Travaux", icon: Hammer, color: "text-blue-600 bg-blue-100" },
    { id: "fournitures", label: "Fournitures", icon: Package, color: "text-amber-600 bg-amber-100" },
    { id: "services", label: "Services", icon: Briefcase, color: "text-emerald-600 bg-emerald-100" }
  ];

  const documentTypes = [
    { id: "rc", label: "RC", fullName: "Règlement de Consultation", color: "bg-blue-500" },
    { id: "ccap", label: "CCAP", fullName: "Clauses Administratives", color: "bg-amber-500" },
    { id: "cctp", label: "CCTP", fullName: "Clauses Techniques", color: "bg-emerald-500" }
  ];

  const modes = [
    { id: "courte", label: "Courte", description: "Résumé concis" },
    { id: "standard", label: "Standard", description: "Analyse complète" },
    { id: "detaillee", label: "Détaillée", description: "Analyse exhaustive" }
  ];

  return (
    <>
      <SEO 
        title="Analyse Gratuite de Documents Marchés Publics"
        description="Testez gratuitement notre IA d'analyse de documents d'appels d'offres. Analysez vos RC, CCAP et CCTP sans inscription. 3 analyses par jour offertes."
        path="/analyse-gratuite"
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
        {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-slate-900">Mon DCE Express</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600">
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6">
                  Créer un compte gratuit
                </Button>
              </Link>
            </div>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <div className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Accueil</Link>
                <Link to="/analyse-dce" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Synthèse IA</Link>
                <Link to="/blog" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/guide-appels-offres" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Guide</Link>
                <Link to="/contact" className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <div className="border-t border-slate-200 mt-2 pt-4 flex flex-col gap-2 px-4">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Connexion</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full">Créer un compte gratuit</Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Badge className="mb-4 bg-violet-100 text-violet-700">
            <Sparkles className="w-3 h-3 mr-1" />
            Analyse gratuite sans inscription
          </Badge>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Synthèse <span className="italic text-violet-600">IA</span> de votre document
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Déposez votre RC, CCAP ou CCTP et obtenez une synthèse structurée en moins de 30 secondes.
            <br />
            <span className="text-sm text-slate-500">3 analyses gratuites par jour • Sans inscription</span>
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upload & Options */}
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  1. Déposez votre document PDF
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    file ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-violet-400 hover:bg-violet-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="font-medium text-green-700">{fileName}</p>
                        <p className="text-sm text-green-600">Document prêt</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Glissez votre PDF ici</p>
                      <p className="text-sm text-slate-500">ou cliquez pour parcourir</p>
                    </>
                  )}
                </div>
              </div>

              {/* Domain Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  2. Type de marché
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {domains.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => setDomain(d.id)}
                      className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                        domain === d.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.color}`}>
                        <d.icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{d.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Document Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  3. Type de document
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {documentTypes.map((dt) => (
                    <button
                      key={dt.id}
                      onClick={() => setDocumentType(dt.id)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        documentType === dt.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Badge className={`${dt.color} text-white mb-1`}>{dt.label}</Badge>
                      <p className="text-xs text-slate-500">{dt.fullName}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Analysis Mode */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  4. Niveau de détail
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {modes.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMode(m.id)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        mode === m.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="font-medium text-slate-700">{m.label}</span>
                      <p className="text-xs text-slate-500 mt-1">{m.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Analyze Button */}
              <Button
                onClick={handleAnalyze}
                disabled={!file || !domain || !documentType || loading}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 text-lg"
                data-testid="analyze-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Analyser le document
                  </>
                )}
              </Button>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{error}</p>
                  {error.includes("5/hour") && (
                    <Link to="/register" className="text-violet-600 text-sm font-medium hover:underline">
                      Créez un compte pour accéder aux plans payants →
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Résultat de l'analyse
              </h3>

              {!result && !loading && (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Déposez un document et lancez l'analyse pour voir les résultats</p>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-violet-600" />
                  <p className="text-slate-600">L'IA analyse votre document...</p>
                  <p className="text-sm text-slate-400 mt-2">Cela peut prendre jusqu'à 30 secondes</p>
                </div>
              )}

              {result && (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {/* Show message if result is empty */}
                  {Object.keys(result).length === 0 && (
                    <div className="text-center py-8 bg-amber-50 rounded-xl border border-amber-200">
                      <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                      <h3 className="font-semibold text-amber-700 mb-2">Aucune information extraite</h3>
                      <p className="text-amber-600 text-sm max-w-md mx-auto">
                        Le document n'a pas pu être analysé correctement. Vérifiez que le PDF contient du texte lisible (non scanné) et réessayez.
                      </p>
                    </div>
                  )}
                  
                  {/* Affichage dynamique de tous les champs retournés */}
                  {Object.keys(result).length > 0 && Object.entries(result).map(([key, value]) => {
                    // Helper to check if value is meaningful
                    const isValueMeaningful = (val) => {
                      if (!val) return false;
                      if (typeof val === 'string') {
                        const lower = val.toLowerCase();
                        return val.trim() !== '' && 
                               !lower.includes('non spécifié') && 
                               !lower.includes('non précisé') &&
                               !lower.includes('non mentionné') &&
                               !lower.includes('non renseigné') &&
                               lower !== 'n/a' &&
                               lower !== 'na';
                      }
                      if (Array.isArray(val)) {
                        return val.length > 0 && val.some(item => isValueMeaningful(item));
                      }
                      if (typeof val === 'object' && val !== null) {
                        return Object.values(val).some(v => isValueMeaningful(v));
                      }
                      return true;
                    };
                    
                    // Skip empty or "Non spécifié" values
                    if (!isValueMeaningful(value)) return null;
                    
                    // Format key for display
                    const formatKey = (k) => {
                      const labels = {
                        id: "Référence",
                        acheteur: "Acheteur",
                        titre: "Titre",
                        titre_operation: "Opération",
                        titre_mission: "Mission",
                        titre_marche: "Marché",
                        linkDCE: "Lien DCE",
                        contact: "Contact",
                        limite: "Date limite",
                        period: "Durée",
                        contenu: "Contenu / Description",
                        cibleZoneGeo: "Zone géographique",
                        volume: "Volume / Montant",
                        descriptif: "Descriptif",
                        criteres: "Critères d'attribution",
                        visite_obligatoire: "Visite obligatoire",
                        avis: "Avis / Appréciation",
                        nature_produits: "Nature des produits",
                        potentiel_marche: "Potentiel du marché",
                        perimetre_geographique: "Périmètre géographique",
                        duree_marche: "Durée du marché",
                        nombre_prestataires_retenus: "Prestataires retenus",
                        description_attentes: "Attentes spécifiques",
                        resume: "Résumé",
                        points_cles: "Points clés",
                        alertes: "Points d'attention",
                        actions: "Actions recommandées",
                        points_vigilance: "Points de vigilance",
                        cadre_juridique: "Cadre juridique",
                        forme_marche: "Forme du marché",
                        prix_et_revision: "Prix et révision",
                        delais_paiement: "Délais de paiement",
                        delais_execution: "Délais d'exécution",
                        delais_livraison: "Délais de livraison",
                        penalites: "Pénalités",
                        reception_travaux: "Réception",
                        garanties: "Garanties",
                        assurances: "Assurances",
                        sous_traitance: "Sous-traitance",
                        resiliation: "Résiliation",
                        normes_et_reglementations: "Normes applicables",
                        materiaux_et_produits: "Matériaux",
                        contraintes_execution: "Contraintes",
                        qualifications_requises: "Qualifications requises",
                        livrables_attendus: "Livrables attendus",
                        specifications_techniques: "Spécifications techniques",
                        garantie_sav: "Garantie / SAV",
                        objet_mission: "Objet de la mission",
                        localisation_chantier: "Localisation",
                        maitre_ouvrage: "Maître d'ouvrage",
                        nature_travaux: "Nature des travaux",
                        description_technique: "Description technique"
                      };
                      return labels[k] || k.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
                    };
                    
                    // Render value based on type, filtering out "Non spécifié" sub-values
                    const renderValue = (val) => {
                      if (typeof val === 'string') {
                        return <p className="text-slate-600 text-sm">{val}</p>;
                      }
                      if (Array.isArray(val)) {
                        const meaningfulItems = val.filter(item => isValueMeaningful(item));
                        if (meaningfulItems.length === 0) return null;
                        return (
                          <ul className="space-y-1">
                            {meaningfulItems.map((item, i) => (
                              <li key={`item-${i}`} className="flex items-start gap-2 text-slate-600 text-sm">
                                <CheckCircle2 className="w-3 h-3 text-green-500 mt-1 flex-shrink-0" />
                                <span>{typeof item === 'object' ? JSON.stringify(item) : item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      if (typeof val === 'object' && val !== null) {
                        const meaningfulEntries = Object.entries(val).filter(([_, v]) => isValueMeaningful(v));
                        if (meaningfulEntries.length === 0) return null;
                        return (
                          <div className="space-y-1 pl-2 border-l-2 border-slate-200">
                            {meaningfulEntries.map(([subKey, subVal]) => (
                              <p key={subKey} className="text-slate-600 text-sm">
                                <span className="font-medium text-slate-700">{formatKey(subKey)}:</span> {String(subVal)}
                              </p>
                            ))}
                          </div>
                        );
                      }
                      return <p className="text-slate-600 text-sm">{String(val)}</p>;
                    };
                    
                    // Determine color based on field type
                    const getColor = (k) => {
                      if (['alertes', 'points_vigilance', 'penalites'].includes(k)) return 'bg-amber-500';
                      if (['actions', 'livrables_attendus'].includes(k)) return 'bg-blue-500';
                      if (['resume', 'contenu', 'objet_mission'].includes(k)) return 'bg-violet-500';
                      return 'bg-slate-400';
                    };
                    
                    return (
                      <div key={key} className="bg-slate-50 p-3 rounded-lg">
                        <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${getColor(key)}`} />
                          {formatKey(key)}
                        </h4>
                        {renderValue(value)}
                      </div>
                    );
                  })}

                  {/* CTA */}
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mt-6">
                    <h4 className="font-medium text-violet-900 mb-2">Besoin de plus d'analyses ?</h4>
                    <p className="text-sm text-violet-700 mb-3">
                      Passez à un plan payant pour des synthèses illimitées et l'export PDF.
                    </p>
                    <Link to="/tarifs-synthese">
                      <Button className="bg-violet-600 hover:bg-violet-700 text-white w-full">
                        Voir les tarifs
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>

                  <Button
                    onClick={resetForm}
                    variant="outline"
                    className="w-full"
                  >
                    Nouvelle analyse
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2026 Mon DCE Express. Analyse gratuite limitée à 3 documents par jour.</p>
          <Link to="/register" className="text-violet-600 hover:underline">
            Créez un compte gratuit pour des analyses illimitées
          </Link>
        </div>
      </footer>
    </div>
    </>
  );
}
